import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
  Grid,
  Chip,
  Avatar,
  Fade,
} from '@mui/material';
import {
  StorefrontTwoTone,
  TrendingUpRounded,
  TrendingDownRounded,
  Token,
  TuneRounded,
  SpeedRounded,
  AccountTreeRounded,
  EditRounded,
} from '@mui/icons-material';
import { PriceDialog } from './Price/PriceDialog';
import { getprofitPercentage } from '../pages/Sell/Sell';
import { useDialogContext } from './dialog-context';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import { services } from '../pages/Sell/services';
import { useEffect, useState } from 'react';

const CurrentPrice = ({
  rows,
  setBullionsConfig,
  bullionsConfig,
  trig,
  trigger,
  pricePerGram,
  profitPercentageText,
  bullionTypes,
  manualPriceLessThanThreshhold,
  manualPriceMoreThanThreshhold,
  isPriceFirstLoading,
}) => {
  const { openDialog, closeDialog } = useDialogContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const firstCategory = bullionTypes.slice(0, 7);
  const secondCategory = bullionTypes.slice(7);
  const [autoPriceSource, setAutoPriceSource] = useState('ESTJT');
  const [priceSources, setPriceSources] = useState([]);

  useEffect(() => {
    services
      .getPriceSource()
      .then(res => {
        if (res?.nameEn) {
          // setPriceSource(res.source);
          setAutoPriceSource(res.nameEn);
        }
      })
      .catch(err => {
        console.error('Error loading price source:', err);
      });
  }, [trigger]);

  useEffect(() => {
    services.getAvailablePriceSources().then(sources => {
      setPriceSources(sources);
    });
  }, [trigger]);

  // --- MODERN SELL THEME ---
  const sellTheme = {
    primary: '#059669', // Emerald 600
    dark: '#064e3b', // Emerald 900
    light: '#d1fae5', // Emerald 100
    bg: '#ffffff',
    hover: '#ecfdf5', // Emerald 50
    border: '#a7f3d0', // Emerald 200
    shadow:
      '0 10px 15px -3px rgba(5, 150, 105, 0.1), 0 4px 6px -2px rgba(5, 150, 105, 0.05)',
    headerGradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    textSecondary: '#64748b',
  };

  // --- STRATEGY BADGE RENDERER ---
  const renderStrategyChip = strategy => {
    let label = strategy;
    let color = 'default';
    let icon = <TuneRounded sx={{ fontSize: 16 }} />;

    if (strategy === 'MANUAL') {
      label = 'دستی';
      color = 'default'; // Grey
      icon = <EditRounded sx={{ fontSize: 16 }} />;
    } else if (strategy === 'AUTOMATIC_COMPETITOR') {
      label = 'رقیب';
      color = 'secondary'; // Purple in default theme, but we can override
      icon = <SpeedRounded sx={{ fontSize: 16 }} />;
    } else if (strategy === 'AUTOMATIC_ESTJT') {
      let source = 'مرجع قیمتی';

      source = priceSources.find(s => s.nameEn === autoPriceSource)?.nameFa;
      label = source;
      color = 'warning';
      icon = <EqualizerIcon sx={{ fontSize: 16 }} />;
    }

    return (
      <Chip
        icon={icon}
        label={
          <Stack direction="column" spacing={0.25} alignItems="flex-start">
            {strategy === 'AUTOMATIC_ESTJT' && (
              <span
                style={{ fontSize: '0.65rem', opacity: 0.85, fontWeight: 500 }}
              >
                مرجع قیمتی
              </span>
            )}
            <span>{label}</span>
          </Stack>
        }
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 'auto', // ارتفاع خودکار

          borderRadius: '8px',

          // اجازه شکستن خط به لیبل داخلی Chip
          '& .MuiChip-label': {
            display: 'block',
            whiteSpace: 'normal',
            px: 1,
          },

          // هماهنگی آیکون هنگام ۲ خطه شدن
          '& .MuiChip-icon': {
            alignSelf: 'center',
            m: 0,
            ml: 0.75,
          },

          // رنگ‌بندی حرفه‌ای و شیک
          bgcolor: 'transparent',
          color:
            strategy === 'AUTOMATIC_COMPETITOR'
              ? '#6d28d9'
              : strategy === 'AUTOMATIC_ESTJT'
                ? '#1d4ed8' // رنگ آبی متین بجای نارنجی هشدار
                : '#475569',

          border: 'none',
          // borderColor:
          //   strategy === 'AUTOMATIC_COMPETITOR'
          //     ? '#ddd6fe'
          //     : strategy === 'AUTOMATIC_ESTJT'
          //       ? '#bfdbfe'
          //       : '#e2e8f0',
        }}
      />
    );
  };

  // --- TABLE HEAD ---
  const renderTableHead = () => (
    <TableHead>
      <TableRow
        sx={{
          background: sellTheme.headerGradient,
          '& th': {
            color: '#fff',
            fontWeight: 700,
            py: 2,
            px: { xs: 1, md: 2 },
            fontSize: '0.85rem',
            borderBottom: 'none',
            whiteSpace: 'nowrap',
            letterSpacing: '0.5px',
            textShadow: '0 1px 2px rgba(0,0,0,0.1)',
            '&:first-of-type': {
              borderTopLeftRadius: '16px',
            },
            '&:last-child': {
              borderTopRightRadius: '16px',
            },
          },
        }}
      >
        <TableCell>شمش</TableCell>
        <TableCell>قیمت فروش</TableCell>
        <TableCell>قیمت تابلو</TableCell>
        <TableCell>تفاوت با مرجع قیمت</TableCell>
        <TableCell>قیمت رقیب</TableCell>
        <TableCell>استراتژی</TableCell>
        <TableCell>هر گرم (دستی)</TableCell>
        <TableCell>هزینه تولید</TableCell>
        <TableCell>حاشیه سود</TableCell>
        <TableCell>گردشده</TableCell>
        <TableCell>توسط</TableCell>
      </TableRow>
    </TableHead>
  );

  // --- SKELETON ---
  const renderSkeletonRows = () =>
    [...Array(6)].map((_, index) => (
      <TableRow key={index}>
        {[...Array(11)].map((_, i) => (
          <TableCell key={i} sx={{ py: 2 }}>
            <Skeleton
              variant="rounded"
              width={i === 0 ? 80 : 50}
              height={24}
              sx={{ borderRadius: 1 }}
            />
          </TableCell>
        ))}
      </TableRow>
    ));

  // --- ROW RENDERER ---
  const renderTableRow = (bullion, index) => {
    const isHavingDiscount = Number(bullion.price) < Number(bullion.boardPrice);

    // Profit Color Logic
    const profitVal = Number(bullion.profitPercentage);
    const profitColor =
      profitVal > 0 ? '#10b981' : profitVal < 0 ? '#ef4444' : '#64748b';
    const profitIcon =
      profitVal > 0 ? (
        <TrendingUpRounded fontSize="small" />
      ) : profitVal < 0 ? (
        <TrendingDownRounded fontSize="small" />
      ) : null;

    return (
      <TableRow
        key={bullion.key}
        onClick={() =>
          openDialog(
            `${bullion.name}`,
            <PriceDialog
              strategyDefault={bullion.strategy}
              transactionType="BUY"
              is995={!!bullion.key.includes('GB_995')}
              costPercentageDefault={bullion.costPercentage}
              manualBasePriceDefault={bullion.manualBasePrice}
              priceDefault={bullion.price}
              differenceWithCompetitorPercentage={
                bullion.differenceWithCompetitorPercentage
              }
              bullionWeight={bullion.weight}
              trig={trig}
              trigger={trigger}
              strategy={bullion.strategy}
              cutNumberDefault={bullion.cutNumber}
              profitPercentageDefault={
                getprofitPercentage(
                  bullionsConfig?.find(b => b.productType === bullion.key)
                ).profitPercentage
              }
              bullionData={bullionsConfig?.find(
                b => b.productType === bullion.key
              )}
              manualPriceLessThanThreshhold={manualPriceLessThanThreshhold}
              manualPriceMoreThanThreshhold={manualPriceMoreThanThreshhold}
              setBullionsConfig={setBullionsConfig}
              onClick={bullion.dialogProps.onClick}
            />
          )
        }
        sx={{
          cursor: 'pointer',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          borderBottom: '1px solid #f1f5f9',
          '&:last-child td': { borderBottom: 0 },
          '&:hover': {
            backgroundColor: sellTheme.hover,
            transform: 'scale(1.002) translateX(-4px)',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)',
            zIndex: 1,
            position: 'relative',
          },
          '& td': {
            color: '#334155',
            fontSize: '0.85rem',
            py: 2.2,
            px: { xs: 1, md: 2 },
            fontWeight: 500,
            whiteSpace: 'nowrap',
          },
        }}
      >
        <TableCell sx={{ fontWeight: 700, color: sellTheme.dark }}>
          {bullion.name}
        </TableCell>

        <TableCell>
          <Tooltip title="قیمت نهایی فروش" arrow placement="top">
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                component="span"
                sx={{
                  fontWeight: isHavingDiscount ? 800 : 600,
                  fontSize: '1rem',
                  color: isHavingDiscount ? '#0d9488' : sellTheme.dark,
                }}
              >
                {Number(bullion.price).toLocaleString('fa-IR')}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                ریال
              </Typography>
            </Box>
          </Tooltip>
        </TableCell>

        <TableCell>
          {bullion.price > 0 && bullion.boardPrice === 0 ? (
            <Chip
              label="بدون تخفیف"
              size="small"
              variant="outlined"
              sx={{
                bgcolor: 'transparent',
                border: 'none',
                color: '#94a3b8',
                height: 25,
                fontSize: '0.85rem',
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: sellTheme.dark,
                }}
              >
                {Number(bullion.boardPrice).toLocaleString('fa-IR')}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                ریال
              </Typography>
            </Box>
          )}
        </TableCell>

        <TableCell>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: Number(bullion.profitMargin) > 0 ? '#16a34a' : '#dc2626',
              bgcolor: Number(bullion.profitMargin) > 0 ? '#dcfce7' : '#fee2e2',
              px: 1,
              py: 0.5,
              borderRadius: '6px',
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              {Number(bullion.profitMargin).toLocaleString('fa-IR')}%
            </Typography>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            color: bullion?.competitorData?.tokeniko_active
              ? '#334155'
              : '#94a3b8',
          }}
        >
          {!!bullion?.competitorData?.tokeniko ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                textDecoration: !bullion.competitorData?.tokeniko_active
                  ? 'line-through'
                  : 'none',
              }}
            >
              {Number(bullion?.competitorData?.tokeniko).toLocaleString(
                'fa-IR'
              )}
            </Box>
          ) : (
            '---'
          )}
        </TableCell>

        <TableCell>{renderStrategyChip(bullion.strategy)}</TableCell>

        <TableCell sx={{ fontFamily: 'monospace' }}>
          {bullion.strategy === 'MANUAL' ? (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: sellTheme.dark,
                }}
              >
                {Number(bullion.manualBasePrice).toLocaleString('fa-IR')}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                ریال
              </Typography>
            </Box>
          ) : (
            '---'
          )}
        </TableCell>

        <TableCell>
          {bullion.costPercentage !== null ? (
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              {Number(bullion.costPercentage ?? 0).toLocaleString('fa-IR')}%
            </Typography>
          ) : (
            '---'
          )}
        </TableCell>

        <TableCell>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: profitColor,
            }}
          >
            {profitIcon}
            <Typography variant="body2" fontWeight={700}>
              {bullion.profitPercentage !== null
                ? `${Number(bullion.profitPercentage ?? 0).toLocaleString('fa-IR')}%`
                : '---'}
            </Typography>
          </Box>
        </TableCell>

        <TableCell>
          {bullion.cutNumber !== null ? (
            <Chip
              label={`${Number(bullion.cutNumber).toLocaleString('fa-IR')} رقم`}
              size="small"
              sx={{ bgcolor: 'transparent', fontWeight: 600, height: 22 }}
            />
          ) : (
            '---'
          )}
        </TableCell>

        <TableCell
          sx={{
            display: 'flex',
            flexDirection: 'column',

            fontWeight: 700,
            color: sellTheme.dark,
          }}
        >
          <Chip
            label={new Date(bullion.createdAt).toLocaleString('fa-IR')}
            size="small"
            sx={{ bgcolor: 'transparent', fontWeight: 600, height: 20 }}
          />
          <Chip
            label={bullion.createdBy}
            size="small"
            sx={{ bgcolor: 'transparent', fontWeight: 600, height: 20 }}
          />
        </TableCell>
      </TableRow>
    );
  };

  const renderDividerRow = () => (
    <TableRow>
      <TableCell colSpan={11} sx={{ p: 0, height: '16px', border: 'none' }} />
    </TableRow>
  );

  return (
    <Grid item xs={12}>
      <Fade in={true} timeout={600}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: '24px',
            border: `1px solid ${sellTheme.border}`,
            backgroundColor: sellTheme.bg,
            boxShadow: sellTheme.shadow,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* --- Header Section --- */}
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              borderBottom: '1px solid #f1f5f9',
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: sellTheme.light,
                color: sellTheme.primary,
                width: 48,
                height: 48,
                borderRadius: '14px',
              }}
            >
              <StorefrontTwoTone fontSize="medium" />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                fontWeight={800}
                color="#1e293b"
                letterSpacing="-0.5px"
              >
                لیست قیمت فروش
              </Typography>
              {/* <Stack direction="row" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#22c55e',
                    boxShadow: '0 0 8px #22c55e',
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  بروزرسانی لحظه‌ای فعال است
                </Typography>
              </Stack> */}
            </Box>
          </Box>

          {/* --- Data Table --- */}
          <TableContainer sx={{ px: 1, pb: 1 }}>
            <Table>
              {!isPriceFirstLoading && renderTableHead()}
              <TableBody>
                {isPriceFirstLoading ? (
                  renderSkeletonRows()
                ) : (
                  <>
                    {firstCategory.map((bullion, index) =>
                      renderTableRow(bullion, index)
                    )}
                    {renderDividerRow()}
                    {secondCategory.map((bullion, index) =>
                      renderTableRow(bullion, index + 7)
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Fade>
    </Grid>
  );
};

export default CurrentPrice;
